/**
 * Low-level Emscripten module interface.
 * Defines the shape of the instantiated Wasm module we interact with.
 */

export interface EmscriptenFS {
  writeFile(path: string, data: Uint8Array | string, opts?: { canOwn?: boolean }): void;
  readFile(path: string, opts?: { encoding?: string }): Uint8Array;
  unlink(path: string): void;
  rename(oldPath: string, newPath: string): void;
  readdir(path: string): string[];
  mkdir(path: string): void;
  stat(path: string): { mode: number };
  lstat(path: string): { mode: number };
  isDir(mode: number): boolean;
  isFile(mode: number): boolean;
  isLink(mode: number): boolean;
  symlink(target: string, path: string): void;
  readlink(path: string): string;
  mount(type: unknown, opts: Record<string, unknown>, mountpoint: string): void;
  unmount(mountpoint: string): void;
  analyzePath(path: string): { exists: boolean; object: unknown };
}

export interface EmscriptenModule {
  FS: EmscriptenFS;
  /**
   * Starts main(). The core is built with -sPROXY_TO_PTHREAD=1, so this
   * returns 0 at once while main() runs on a pthread; the real exit code
   * arrives through onExit. Run it via CoreSession rather than directly.
   */
  callMain(args: string[]): number;
  /** Called with main()'s exit code once the runtime exits. */
  onExit?(code: number): void;
  /** Called if the runtime aborts instead of exiting. */
  onAbort?(what: unknown): void;
  cwrap(name: string, returnType: string | null, argTypes: string[]): (...args: unknown[]) => unknown;
  ccall(name: string, returnType: string | null, argTypes: string[], args: unknown[]): unknown;

  /** Print callback (stdout) */
  print?(text: string): void;
  /** Print error callback (stderr) */
  printErr?(text: string): void;

  /** Called when the module is fully initialized */
  onRuntimeInitialized?(): void;

  /** Allocate memory */
  _malloc(size: number): number;
  /** Free memory */
  _free(ptr: number): void;

  /** HEAP views */
  HEAPU8: Uint8Array;
  HEAP32: Int32Array;
}

export interface CoreFactory {
  (moduleOverrides?: Partial<EmscriptenModule>): Promise<EmscriptenModule>;
}

/**
 * Creates module overrides that hook into FFmpeg's stdout/stderr
 * for log and progress event forwarding.
 */
export function createModuleOverrides(
  onLog?: (type: 'ffout' | 'fferr', message: string) => void,
  onProgress?: (progress: number, time: number) => void,
): Partial<EmscriptenModule> {
  const progressRegex = /time=(\d+):(\d+):(\d+\.\d+)/;
  let duration = 0;

  return {
    print(text: string) {
      onLog?.('ffout', text);
    },
    printErr(text: string) {
      onLog?.('fferr', text);

      // Parse duration from input info
      const durMatch = text.match(/Duration:\s*(\d+):(\d+):(\d+\.\d+)/);
      if (durMatch) {
        duration =
          parseInt(durMatch[1], 10) * 3600 +
          parseInt(durMatch[2], 10) * 60 +
          parseFloat(durMatch[3]);
      }

      // Parse progress from encoding output
      const progMatch = text.match(progressRegex);
      if (progMatch && duration > 0) {
        const currentTime =
          parseInt(progMatch[1], 10) * 3600 +
          parseInt(progMatch[2], 10) * 60 +
          parseFloat(progMatch[3]);
        onProgress?.(Math.min(currentTime / duration, 1), currentTime);
      }
    },
  };
}
